import {announce} from '@github-ui/aria-live'
import {addSortToQuery, getSortFromQuery} from '@github-ui/dashboard-sorting-dropdown/query'
import {isLoggedIn} from '@github-ui/client-env'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {tokenizeQuery} from '@github-ui/filter-query'
import {hpcAttrs} from '@github-ui/hpc'
import {PercentageCircle} from '@github-ui/percentage-circle'
import {getQueryClient} from '@github-ui/query-client'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {Link as PreloadableLink, matchPath, useLocation, useNavigation} from '@github-ui/react-router'
import {PullsList, type PullsListSelection} from '@github-ui/shared-dashboard-lists/pulls/PullsList'
import type {EnrichedPullListItem} from '@github-ui/shared-dashboard-lists/pulls/types/list-types'
import {SharedDashboardBlankslate} from '@github-ui/shared-dashboard-lists/shared/SharedDashboardBlankslate'
import type {StateFilter} from '@github-ui/shared-dashboard-lists/shared/types/list-types'
import {
  addStateToQueryWithLegacyAliases,
  analyzeStateQuery,
} from '@github-ui/shared-dashboard-surface/helpers/open-closed-toggle'
import {
  addAssigneeFilterToQuery,
  addAuthorFilterToQuery,
  addLabelFilterToQuery,
} from '@github-ui/shared-dashboard-surface/helpers/query-filters'
import {useScreenReaderAnnouncements} from '@github-ui/shared-dashboard-surface/hooks/use-screen-reader-announcements'
import {useUpdateCompactModeSetting} from '@github-ui/shared-dashboard-surface/hooks/use-update-compact-mode-setting'
import {sendCustomMetric} from '@github-ui/stats'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {useAnalytics} from '@github-ui/use-analytics'
import {Pagination} from '@primer/react'
import {useCallback, useEffect, useMemo, useRef, useState, type RefObject} from 'react'

import {RepoPullsBulkLabels} from '../components/RepoPullsBulkLabels'
import {RepoPullsBulkMarkAs} from '../components/RepoPullsBulkMarkAs'
import {
  RepoPullsBulkAssignees,
  RepoPullsBulkMilestone,
  RepoPullsBulkProjects,
} from '../components/RepoPullsBulkMetadata'
import {getPullRequestTitleLinkProps} from '../helpers/get-pull-request-title-link-props'
import type {RepoPullsBulkUpdate} from '../helpers/repo-pulls-bulk-update'
import {useRepoPullsDashboardData, useRepoPullsSearch} from '../hooks/use-repo-pulls-dashboard'
import {useRepoPullsBulkUpdate} from '../mutations/use-repo-pulls-bulk-update'
import {repoPullsDashboardLayoutRoute} from './repo-pulls-dashboard-layout-route'
import type {RepoPullsQuickFilterKind} from './repo-pulls-quick-filters/quick-filter-shared'
import {useRepoPullsQuickFilterItems} from './repo-pulls-quick-filters/use-repo-pulls-quick-filter-actions'
import {replaceReviewFilter} from './repo-pulls-quick-filters/ReviewQuickFilter'

const REPO_PULLS_DEFAULT_SORTING_KEY = 'created'
const EMPTY_PULLS: EnrichedPullListItem[] = []
type RepoPullsFilterKind = RepoPullsQuickFilterKind | 'sort'
type RepoPullsFilterSource = 'list' | 'toolbar'

const FILTER_KEYS: Record<Exclude<RepoPullsFilterKind, 'sort'>, readonly string[]> = {
  assignee: ['assignee'],
  author: ['author'],
  label: ['label'],
  milestone: ['milestone'],
  projects: ['project'],
  reviews: ['review', 'reviewed-by', 'review-requested', 'user-review-requested'],
}

function getFilterState(query: string, filter: RepoPullsFilterKind): string | undefined {
  if (filter === 'sort') return getSortFromQuery(query)
  if (query.length > 1000) return undefined

  const keys = FILTER_KEYS[filter]
  return tokenizeQuery(query)
    .filter(segment => segment.type === 'filter' && keys.includes(segment.key.toLowerCase()))
    .flatMap(segment =>
      segment.type === 'filter'
        ? segment.values.map(value => `${segment.isNegated ? '-' : ''}${segment.key.toLowerCase()}:${value}`)
        : [],
    )
    .toSorted()
    .join(' ')
}

function hasSameFilterState(currentQuery: string, nextQuery: string, filter: RepoPullsFilterKind) {
  const currentState = getFilterState(currentQuery, filter)
  const nextState = getFilterState(nextQuery, filter)
  return currentState === undefined || nextState === undefined ? currentQuery === nextQuery : currentState === nextState
}

const EMPTY_SELECTED_PULL_NUMBERS = new Set<number>()

function isSameLocation(a: {pathname: string; search: string}, b: {pathname: string; search: string}) {
  return a.pathname === b.pathname && a.search === b.search
}

function isPreserveListNavigation(state: unknown) {
  return Boolean(
    state && typeof state === 'object' && 'preserveList' in state && (state as {preserveList?: unknown}).preserveList,
  )
}

function addRepoStateToQuery(query: string, state: StateFilter) {
  return addStateToQueryWithLegacyAliases(query, state)
}

export function RepoPullsDashboardContent() {
  const location = useLocation()
  const navigation = useNavigation()
  const {clearPersistedToast} = useToastContext()
  const bulkUpdateAbortControllerRef = useRef<AbortController | null>(null)
  const navigationLocation = navigation.location ?? location
  const currentDashboardMatch = matchPath('/:owner/:repo/pulls/*', location.pathname)
  const navigationDashboardMatch = matchPath('/:owner/:repo/pulls/*', navigationLocation.pathname)
  const isLeavingDashboard =
    !navigationDashboardMatch ||
    navigationDashboardMatch.params.owner !== currentDashboardMatch?.params.owner ||
    navigationDashboardMatch.params.repo !== currentDashboardMatch?.params.repo
  const isNavigating = Boolean(
    navigation.location &&
    matchPath('/:owner/:repo/pulls/*', navigation.location.pathname) &&
    !isSameLocation(navigation.location, location) &&
    !isPreserveListNavigation(navigation.location.state),
  )
  const cancelBulkUpdate = useCallback(() => {
    const abortController = bulkUpdateAbortControllerRef.current
    if (!abortController) return
    bulkUpdateAbortControllerRef.current = null
    abortController.abort()
    clearPersistedToast()
  }, [clearPersistedToast])

  useEffect(() => {
    if (isLeavingDashboard) cancelBulkUpdate()
  }, [cancelBulkUpdate, isLeavingDashboard])

  useEffect(() => cancelBulkUpdate, [cancelBulkUpdate])

  return (
    <ErrorBoundary
      boundaryName="RepoPullsDashboardContent"
      fallback={
        <div className="container-xl px-3 px-md-4 px-lg-5 tmp-my-3">
          <SharedDashboardBlankslate dashboardType="pull-requests" variant="error" />
        </div>
      }
    >
      <RepoPullsDashboardPage bulkUpdateAbortControllerRef={bulkUpdateAbortControllerRef} isNavigating={isNavigating} />
    </ErrorBoundary>
  )
}

function RepoPullsDashboardPage({
  bulkUpdateAbortControllerRef,
  isNavigating,
}: {
  bulkUpdateAbortControllerRef: RefObject<AbortController | null>
  isNavigating: boolean
}) {
  const {data: layoutPayload} = useRouteQuery(repoPullsDashboardLayoutRoute, 'mainQuery')
  const {contentQueryKey, error, isDeferredLoading, pageInfo, pulls, stateCounts} = useRepoPullsDashboardData()
  const {
    analyticsView,
    currentPage,
    fetchedQuery,
    getPageHref,
    hrefForQuery,
    milestone,
    navigateToResults,
    owner,
    repo,
  } = useRepoPullsSearch()
  const {sendAnalyticsEvent} = useAnalytics()
  const {addPersistedToast, addToast, clearPersistedToast} = useToastContext()
  const initialViewRef = useRef(analyticsView)
  const sentPageViewRef = useRef(false)
  const compactMode = layoutPayload?.compactMode ?? false
  const stateQueryAnalysis = analyzeStateQuery(fetchedQuery, {legacyAliases: true})
  const canBulkUpdate = layoutPayload?.actions?.canBulkUpdate ?? false
  const canBulkAddToProjects = layoutPayload?.actions?.canBulkAddToProjects ?? false
  const canBulkArchive = layoutPayload?.actions?.canBulkArchive ?? false
  const pullNumberKey = pulls
    .map(pull => pull.number)
    .sort((a, b) => a - b)
    .join(',')
  const selectionContextKey = `${owner}/${repo}:${currentPage}:${fetchedQuery}:${milestone}`
  const selectionKey = `${selectionContextKey}:${pullNumberKey}`
  const [selection, setSelection] = useState<{key: string; pullNumbers: Set<number>}>(() => ({
    key: selectionKey,
    pullNumbers: new Set(),
  }))
  if (selection.key !== selectionKey) {
    setSelection({key: selectionKey, pullNumbers: new Set()})
  }
  const selectedPullNumbers = selection.key === selectionKey ? selection.pullNumbers : EMPTY_SELECTED_PULL_NUMBERS
  const reportBulkActionError = useCallback(
    (message: string) => {
      clearPersistedToast()
      // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
      addToast({message, type: 'error', role: 'alert'})
      announce(message, {assertive: true})
    },
    [addToast, clearPersistedToast],
  )
  const selectAllCheckboxRef = useRef<HTMLInputElement>(null)
  const restoreSelectionFocusIfLost = useCallback(() => {
    requestAnimationFrame(() => {
      if (document.activeElement === document.body) selectAllCheckboxRef.current?.focus()
    })
  }, [])
  const showRepositoryNavigation = Boolean(layoutPayload?.actions?.labels)

  useEffect(() => {
    if (sentPageViewRef.current) return
    sentPageViewRef.current = true
    sendAnalyticsEvent('pull-requests.page_view', 'REPO_PULLS_DASHBOARD', {view: initialViewRef.current})
    sendCustomMetric({
      name: 'PULL_REQUESTS_REPO_PULLS_DASHBOARD_PAGE_VIEW',
      value: 1,
      tags: {view: initialViewRef.current},
    })
  }, [sendAnalyticsEvent])

  function sendFilterAnalytics(filter: RepoPullsFilterKind, source: RepoPullsFilterSource) {
    sendAnalyticsEvent('pull-requests.click', 'FILTER_CHANGE', {filter, source})
    sendCustomMetric({name: 'PULL_REQUESTS_REPO_PULLS_DASHBOARD_FILTER_CHANGE', value: 1, tags: {filter, source}})
  }

  function applyQueryFilter({
    query,
    filter,
    source,
  }: {
    query: string
    filter: RepoPullsFilterKind
    source: RepoPullsFilterSource
  }) {
    if (hasSameFilterState(fetchedQuery, query, filter)) return
    navigateToResults({q: query})
    sendFilterAnalytics(filter, source)
  }

  const quickFilterItems = useRepoPullsQuickFilterItems({
    owner: owner ?? '',
    repo: repo ?? '',
    query: fetchedQuery,
    onQueryChange: (query, filter) => applyQueryFilter({query, filter, source: 'toolbar'}),
    showLabels: showRepositoryNavigation,
    showMilestones: showRepositoryNavigation,
    showProjects: showRepositoryNavigation,
  })

  const {emptyListState} = useScreenReaderAnnouncements(
    'pull-requests',
    isDeferredLoading,
    currentPage,
    fetchedQuery,
    REPO_PULLS_DEFAULT_SORTING_KEY,
    pageInfo.totalCount,
    pageInfo.totalPages,
    error ?? undefined,
  )
  const {updateCompactModeSetting} = useUpdateCompactModeSetting(compactMode, 'dashboard_surface_compact_mode', {
    persistAsPullsPreference: true,
  })

  const handlePullSelect = useCallback(
    (pullNumber: number, isSelected: boolean) => {
      setSelection(current => {
        const pullNumbers = new Set(current.key === selectionKey ? current.pullNumbers : [])
        if (isSelected) pullNumbers.add(pullNumber)
        else pullNumbers.delete(pullNumber)
        return {key: selectionKey, pullNumbers}
      })
    },
    [selectionKey],
  )

  const handleToggleSelectAll = useCallback(
    (isSelected: boolean) => {
      setSelection({
        key: selectionKey,
        pullNumbers: isSelected ? new Set(pulls.map(pull => pull.number)) : new Set(),
      })
    },
    [pulls, selectionKey],
  )

  const selectedPullNumberList = useMemo(() => [...selectedPullNumbers], [selectedPullNumbers])
  const selectedPulls = useMemo(
    () => pulls.filter(pull => selectedPullNumbers.has(pull.number)),
    [pulls, selectedPullNumbers],
  )
  const currentSelectionViewRef = useRef({
    contextKey: selectionContextKey,
    selectionKey,
    visiblePullNumbers: new Set(pulls.map(pull => pull.number)),
  })
  useEffect(() => {
    currentSelectionViewRef.current = {
      contextKey: selectionContextKey,
      selectionKey,
      visiblePullNumbers: new Set(pulls.map(pull => pull.number)),
    }
  }, [pulls, selectionContextKey, selectionKey])
  const bulkUpdateMutation = useRepoPullsBulkUpdate()
  const handleBulkUpdate = useCallback(
    (update: RepoPullsBulkUpdate) => {
      bulkUpdateAbortControllerRef.current?.abort()
      const abortController = new AbortController()
      bulkUpdateAbortControllerRef.current = abortController
      const submittedPullNumbers = [...selectedPullNumberList]
      const submittedPulls = selectedPulls.flatMap(pull =>
        pull.globalId ? [{nodeId: pull.globalId, number: pull.number}] : [],
      )
      const submittedSelectionContextKey = selectionContextKey
      const progressMessage = `Updating ${submittedPullNumbers.length} pull request${submittedPullNumbers.length === 1 ? '' : 's'}`
      let latestPercentage = 0
      // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
      addPersistedToast({message: progressMessage, type: 'info'})
      announce(progressMessage)
      bulkUpdateMutation.mutate(
        {
          owner,
          repo,
          pullNumbers: submittedPullNumbers,
          pullNodeIds: submittedPulls.map(pull => pull.nodeId),
          update,
          signal: abortController.signal,
          onProgress: percentage => {
            if (percentage <= latestPercentage) return
            latestPercentage = percentage
            // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
            addPersistedToast({
              message: `Bulk update is in progress ${percentage} %`,
              icon: <PercentageCircle progress={percentage / 100} />,
              type: 'info',
              role: 'status',
            })
          },
        },
        {
          onSuccess: async ({failedPullNodeIds}) => {
            if (abortController.signal.aborted) return
            const failedNodeIdSet = new Set(failedPullNodeIds)
            const failedPullNumbers = submittedPulls
              .filter(pull => failedNodeIdSet.has(pull.nodeId))
              .map(pull => pull.number)
            const failedCount = failedPullNumbers.length
            const partialFailureMessage =
              failedCount > 0
                ? `Could not update ${failedCount} of ${submittedPullNumbers.length} pull request${submittedPullNumbers.length === 1 ? '' : 's'}`
                : null
            try {
              await getQueryClient().refetchQueries(
                {queryKey: contentQueryKey, exact: true, type: 'all'},
                {throwOnError: true},
              )
            } catch {
              if (abortController.signal.aborted) return
              reportBulkActionError(
                partialFailureMessage ?? 'Pull requests updated, but the results could not be refreshed',
              )
              const currentView = currentSelectionViewRef.current
              if (currentView.contextKey === submittedSelectionContextKey) {
                if (partialFailureMessage) {
                  setSelection({
                    key: currentView.selectionKey,
                    pullNumbers: new Set(failedPullNumbers),
                  })
                }
                restoreSelectionFocusIfLost()
              }
              return
            }

            await new Promise<void>(resolve => requestAnimationFrame(() => resolve()))
            if (abortController.signal.aborted) return

            if (partialFailureMessage) {
              const currentView = currentSelectionViewRef.current
              if (currentView.contextKey === submittedSelectionContextKey) {
                setSelection({
                  key: currentView.selectionKey,
                  pullNumbers: new Set(
                    failedPullNumbers.filter(pullNumber => currentView.visiblePullNumbers.has(pullNumber)),
                  ),
                })
                restoreSelectionFocusIfLost()
              }
              reportBulkActionError(partialFailureMessage)
              return
            }

            const currentView = currentSelectionViewRef.current
            if (currentView.contextKey === submittedSelectionContextKey) {
              restoreSelectionFocusIfLost()
            }
            const successMessage = `${submittedPullNumbers.length} pull request${submittedPullNumbers.length === 1 ? '' : 's'} updated`
            clearPersistedToast()
            // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
            addToast({message: successMessage, type: 'success'})
            announce(successMessage)
          },
          onError: async mutationError => {
            if (abortController.signal.aborted || mutationError.name === 'AbortError') return
            reportBulkActionError('Could not update pull requests')
            try {
              await getQueryClient().refetchQueries(
                {queryKey: contentQueryKey, exact: true, type: 'all'},
                {throwOnError: true},
              )
            } catch {
              // The update error is primary; a failed best-effort refresh does not replace it.
            }
            if (currentSelectionViewRef.current.contextKey === submittedSelectionContextKey) {
              restoreSelectionFocusIfLost()
            }
          },
          onSettled: () => {
            if (bulkUpdateAbortControllerRef.current === abortController) {
              bulkUpdateAbortControllerRef.current = null
            }
          },
        },
      )
    },
    [
      addPersistedToast,
      addToast,
      bulkUpdateAbortControllerRef,
      bulkUpdateMutation,
      clearPersistedToast,
      contentQueryKey,
      owner,
      repo,
      reportBulkActionError,
      restoreSelectionFocusIfLost,
      selectedPullNumberList,
      selectedPulls,
      selectionContextKey,
    ],
  )
  const commonLabels = useMemo(() => {
    const firstPull = selectedPulls[0]
    if (!firstPull) return []
    return firstPull.labels
      .filter(label => selectedPulls.every(pull => pull.labels.some(candidate => candidate.id === label.id)))
      .map(label => ({id: String(label.id), name: label.name}))
  }, [selectedPulls])
  const {commonAssigneeLogins, partiallyAssignedAssignees} = useMemo(() => {
    const assigneesByLogin = new Map<string, {avatarUrl: string; count: number}>()
    for (const pull of selectedPulls) {
      for (const assignee of pull.assignees ?? []) {
        const current = assigneesByLogin.get(assignee.login)
        assigneesByLogin.set(assignee.login, {
          avatarUrl: assignee.avatarUrl,
          count: (current?.count ?? 0) + 1,
        })
      }
    }

    const common: string[] = []
    const partial: Array<{avatarUrl: string; login: string}> = []
    for (const [login, assignee] of assigneesByLogin) {
      if (assignee.count === selectedPulls.length) common.push(login)
      else partial.push({avatarUrl: assignee.avatarUrl, login})
    }

    return {commonAssigneeLogins: common, partiallyAssignedAssignees: partial}
  }, [selectedPulls])
  const canMarkAsOpen = selectedPulls.every(pull => pull.state !== 'MERGED')
  const showMarkAsClosed = selectedPulls.some(pull => pull.state === 'OPEN')
  const showMarkAsOpen = selectedPulls.some(pull => pull.state !== 'OPEN')
  const bulkActions = useMemo<PullsListSelection['actions']>(() => {
    if (!canBulkUpdate || !owner || !repo || selectedPullNumberList.length === 0) return undefined
    const actions: NonNullable<PullsListSelection['actions']> = [
      {
        key: 'mark-as',
        render: nested => (
          <RepoPullsBulkMarkAs
            canArchive={canBulkArchive}
            canMarkAsOpen={canMarkAsOpen}
            disabled={bulkUpdateMutation.isPending}
            nested={nested}
            onUpdate={handleBulkUpdate}
            showMarkAsClosed={showMarkAsClosed}
            showMarkAsOpen={showMarkAsOpen}
          />
        ),
      },
      {
        key: 'labels',
        render: nested => (
          <RepoPullsBulkLabels
            commonLabels={commonLabels}
            disabled={bulkUpdateMutation.isPending}
            nested={nested}
            owner={owner}
            repo={repo}
            onUpdate={handleBulkUpdate}
          />
        ),
      },
    ]
    if (canBulkAddToProjects) {
      actions.push({
        key: 'projects',
        render: nested => (
          <RepoPullsBulkProjects
            disabled={bulkUpdateMutation.isPending}
            nested={nested}
            owner={owner}
            repo={repo}
            onUpdate={handleBulkUpdate}
          />
        ),
      })
    }
    actions.push(
      {
        key: 'milestone',
        render: nested => (
          <RepoPullsBulkMilestone
            disabled={bulkUpdateMutation.isPending}
            nested={nested}
            owner={owner}
            repo={repo}
            onUpdate={handleBulkUpdate}
          />
        ),
      },
      {
        key: 'assignees',
        render: nested => (
          <RepoPullsBulkAssignees
            commonAssigneeLogins={commonAssigneeLogins}
            disabled={bulkUpdateMutation.isPending}
            nested={nested}
            owner={owner}
            partiallyAssignedAssignees={partiallyAssignedAssignees}
            repo={repo}
            onUpdate={handleBulkUpdate}
          />
        ),
      },
    )
    return actions
  }, [
    canBulkAddToProjects,
    canBulkArchive,
    canBulkUpdate,
    canMarkAsOpen,
    commonAssigneeLogins,
    commonLabels,
    bulkUpdateMutation.isPending,
    handleBulkUpdate,
    owner,
    partiallyAssignedAssignees,
    repo,
    selectedPullNumberList,
    showMarkAsClosed,
    showMarkAsOpen,
  ])

  function handleFilterSelect(type: 'repo' | 'author' | 'label' | 'assignee', value: string) {
    let nextQuery = fetchedQuery
    switch (type) {
      case 'repo':
        return
      case 'author':
        nextQuery = addAuthorFilterToQuery(fetchedQuery, value)
        break
      case 'label':
        nextQuery = addLabelFilterToQuery(fetchedQuery, value)
        break
      case 'assignee':
        nextQuery = addAssigneeFilterToQuery(fetchedQuery, value)
        break
    }
    applyQueryFilter({query: nextQuery, filter: type, source: 'list'})
  }

  function getAuthorLinkProps(login: string) {
    const nextQuery = addAuthorFilterToQuery(fetchedQuery, login)
    return {
      as: PreloadableLink,
      to: hrefForQuery(nextQuery),
      reloadDocument: false,
      state: {preserveList: true},
      onClick: hasSameFilterState(fetchedQuery, nextQuery, 'author')
        ? undefined
        : () => sendFilterAnalytics('author', 'list'),
    }
  }

  function getStateFilterLinkProps(state: StateFilter) {
    return {
      to: hrefForQuery(addRepoStateToQuery(fetchedQuery, state)),
      reloadDocument: false,
    }
  }

  return (
    <div>
      <div className="tmp-my-3" {...hpcAttrs()}>
        {error ? (
          <SharedDashboardBlankslate dashboardType="pull-requests" variant="error" />
        ) : (
          <>
            <PullsList
              pulls={isNavigating ? EMPTY_PULLS : pulls}
              pageInfo={pageInfo}
              compactMode={compactMode}
              onCompactModeChange={updateCompactModeSetting}
              onSortingItemSelect={sort =>
                applyQueryFilter({query: addSortToQuery(fetchedQuery, sort), filter: 'sort', source: 'toolbar'})
              }
              sortingItemSelected={getSortFromQuery(fetchedQuery)}
              stateFilters={
                stateQueryAnalysis.kind === 'ambiguous'
                  ? {kind: 'hidden'}
                  : {
                      kind: 'visible',
                      selected: stateQueryAnalysis.kind === 'selected' ? stateQueryAnalysis.state : undefined,
                      counts: stateCounts,
                      getLinkProps: getStateFilterLinkProps,
                    }
              }
              emptyListState={emptyListState}
              onFilterSelect={handleFilterSelect}
              onReviewFilterSelect={value =>
                applyQueryFilter({
                  query: replaceReviewFilter(fetchedQuery, {key: 'review', value}),
                  filter: 'reviews',
                  source: 'list',
                })
              }
              isLoading={isNavigating}
              isDeferredLoading={isDeferredLoading}
              isAgentAttributionEnabled={isFeatureEnabled('agent_author_search_expansion_ui_pulls')}
              showUnreadIndicator={isLoggedIn()}
              defaultSortingKey={REPO_PULLS_DEFAULT_SORTING_KEY}
              showRepository={false}
              actionBarItems={owner && repo ? quickFilterItems : undefined}
              getTitleLinkProps={getPullRequestTitleLinkProps}
              selection={
                canBulkUpdate
                  ? {
                      selectedPullNumbers,
                      disabled: bulkUpdateMutation.isPending,
                      selectAllCheckboxRef,
                      onPullSelect: handlePullSelect,
                      onToggleSelectAll: handleToggleSelectAll,
                      actions: bulkActions,
                    }
                  : undefined
              }
              getAuthorLinkProps={getAuthorLinkProps}
            />
            {pageInfo.totalPages > 1 ? (
              <Pagination
                pageCount={pageInfo.totalPages}
                currentPage={pageInfo.currentPage}
                hrefBuilder={getPageHref}
                renderPage={({key, number, href, ...props}) => (
                  <PreloadableLink key={key} {...props} to={href ?? getPageHref(number)} preload="intent" />
                )}
                onPageChange={(event, page) => {
                  event.preventDefault()
                  navigateToResults({q: fetchedQuery, page})
                }}
              />
            ) : null}
          </>
        )}
      </div>
    </div>
  )
}
